/**
 * usePipeline.ts — Estado del PIPELINE / KANBAN de prospección, conectado al
 * backend propio `/api/v1/listings/pipeline` (KANBAN-PIPELINE).
 *
 *   - Carga `GET /pipeline?order_by=...` (tarjetas con datos del negocio en raíz
 *     + seguimiento CRM). El criterio de orden se persiste por usuario.
 *   - `move(placeId, stage)` → PATCH con UI optimista + revertir si falla.
 *   - `remove(placeId)`     → DELETE con UI optimista + revertir si falla.
 *   - `setNextStep(placeId, {text, due})` → PATCH del próximo paso (UI optimista).
 *   - `add(placeIds, stage?)`→ POST (UPSERT) y recarga para traer las tarjetas
 *     enriquecidas (POST no devuelve los datos del negocio).
 *   - `inPipeline(id)` / `stageOf(id)` / `cardOf(id)` para la ficha y la tabla.
 *
 * Bajo `?preview` (DEV, sin backend) cae a `DEMO_PIPELINE` y las mutaciones son
 * solo optimistas (no hay servidor que persista).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  getPipeline,
  addToPipeline,
  updatePipelineStage,
  updatePipelineItem,
  removeFromPipeline,
  type PipelineCard,
  type PipelineStage,
  type PipelineOrderBy,
  type NextStepStatus,
} from '@/services/listingsService'
import { DEMO_PIPELINE } from '@/pages/dashboardDemo'

const IS_PREVIEW =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('preview')

const ORDER_BY_STORAGE_KEY = 'mapeo.pipeline.orderBy'
const VALID_ORDER_BY: PipelineOrderBy[] = ['next_action', 'added', 'score']

function loadOrderBy(): PipelineOrderBy {
  try {
    const raw = localStorage.getItem(ORDER_BY_STORAGE_KEY)
    if (raw && (VALID_ORDER_BY as string[]).includes(raw)) return raw as PipelineOrderBy
  } catch {
    // ignore
  }
  return 'next_action'
}

/** Estado de "calor" del próximo paso a partir de la fecha (cálculo local,
 *  coherente con el backend) para la UI optimista al editar el próximo paso. */
function deriveNextStatus(due: string | null): NextStepStatus {
  if (!due) return 'no_next_step'
  const today = new Date()
  const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  if (due < todayYmd) return 'overdue'
  if (due === todayYmd) return 'today'
  return 'future'
}

export interface UsePipeline {
  cards: PipelineCard[]
  loading: boolean
  loaded: boolean
  error: string | null
  orderBy: PipelineOrderBy
  setOrderBy: (o: PipelineOrderBy) => void
  inPipeline: (placeId: string) => boolean
  stageOf: (placeId: string) => PipelineStage | null
  cardOf: (placeId: string) => PipelineCard | null
  reload: () => void
  move: (placeId: string, stage: PipelineStage) => Promise<void>
  remove: (placeId: string) => Promise<void>
  setNextStep: (placeId: string, next: { text: string | null; due: string | null }) => Promise<void>
  /** Devuelve cuántos place_ids se procesaron. */
  add: (placeIds: string[], stage?: 'nuevo' | 'contactado') => Promise<number>
}

export function usePipeline(): UsePipeline {
  const [cards, setCards] = useState<PipelineCard[]>([])
  const [loading, setLoading] = useState(true)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [orderBy, setOrderByState] = useState<PipelineOrderBy>(loadOrderBy)

  // Ref con las tarjetas vivas para leer el estado previo en las reversiones
  // sin depender del closure de cada handler.
  const cardsRef = useRef<PipelineCard[]>([])
  cardsRef.current = cards

  const byId = useMemo(() => {
    const m = new Map<string, PipelineCard>()
    for (const c of cards) m.set(c.place_id, c)
    return m
  }, [cards])

  const reload = useCallback(async (signal?: AbortSignal, order: PipelineOrderBy = orderBy) => {
    setLoading(true)
    setError(null)
    try {
      const resp = await getPipeline(order, signal)
      setCards(resp.items)
      setLoaded(true)
    } catch (e) {
      if ((e as { code?: string })?.code === 'aborted') return
      if (IS_PREVIEW) {
        setCards(DEMO_PIPELINE)
        setLoaded(true)
        return
      }
      setError('No se pudo cargar tu pipeline. Reintenta.')
      setLoaded(true)
    } finally {
      setLoading(false)
    }
  }, [orderBy])

  useEffect(() => {
    const ctrl = new AbortController()
    reload(ctrl.signal, orderBy)
    return () => ctrl.abort()
  }, [reload, orderBy])

  const setOrderBy = useCallback((o: PipelineOrderBy) => {
    setOrderByState(o)
    try {
      localStorage.setItem(ORDER_BY_STORAGE_KEY, o)
    } catch {
      // ignore
    }
  }, [])

  const move = useCallback(async (placeId: string, stage: PipelineStage) => {
    const prev = cardsRef.current.find((c) => c.place_id === placeId)?.stage
    if (prev === stage) return
    setCards((cs) => cs.map((c) => (c.place_id === placeId ? { ...c, stage } : c)))
    if (IS_PREVIEW) return
    try {
      await updatePipelineStage(placeId, stage)
    } catch {
      // Revertir al stage anterior.
      if (prev) setCards((cs) => cs.map((c) => (c.place_id === placeId ? { ...c, stage: prev } : c)))
    }
  }, [])

  const remove = useCallback(async (placeId: string) => {
    const removed = cardsRef.current.find((c) => c.place_id === placeId)
    setCards((cs) => cs.filter((c) => c.place_id !== placeId))
    if (IS_PREVIEW) return
    try {
      await removeFromPipeline(placeId)
    } catch {
      if (removed) setCards((cs) => [removed, ...cs])
    }
  }, [])

  const setNextStep = useCallback(
    async (placeId: string, next: { text: string | null; due: string | null }) => {
      const prev = cardsRef.current.find((c) => c.place_id === placeId)
      const status = deriveNextStatus(next.due)
      setCards((cs) =>
        cs.map((c) =>
          c.place_id === placeId
            ? { ...c, next_step_text: next.text, next_step_due: next.due, next_step_status: status }
            : c,
        ),
      )
      if (IS_PREVIEW) return
      try {
        await updatePipelineItem(placeId, { next_step_text: next.text, next_step_due: next.due })
      } catch {
        if (prev) {
          setCards((cs) => cs.map((c) => (c.place_id === placeId ? prev : c)))
        }
      }
    },
    [],
  )

  const add = useCallback(
    async (placeIds: string[], stage?: 'nuevo' | 'contactado') => {
      if (placeIds.length === 0) return 0
      if (IS_PREVIEW) {
        // Sin backend: si hay stage explícito, mueve los que ya estén.
        if (stage) {
          setCards((cs) => cs.map((c) => (placeIds.includes(c.place_id) ? { ...c, stage } : c)))
        }
        return placeIds.length
      }
      const res = await addToPipeline(placeIds, stage)
      await reload()
      return res.count
    },
    [reload],
  )

  return {
    cards,
    loading,
    loaded,
    error,
    orderBy,
    setOrderBy,
    inPipeline: (id) => byId.has(id),
    stageOf: (id) => byId.get(id)?.stage ?? null,
    cardOf: (id) => byId.get(id) ?? null,
    reload: () => reload(),
    move,
    remove,
    setNextStep,
    add,
  }
}
